import React from 'react';
import * as Cookies from 'js-cookie';
import { toggleFavourite } from '../components/Favourites';

export default function PlotCard({
  className,
  isOpen,
  plot,
  onPlotChange,
  onPlotClose,
}) {
  if (!plot) {
    return (
      <div className={`siteplan__card${className ? ` ${className}` : ''}`} />
    );
  }

  const getFavouritesList = () => {
    const storedFavourites = Cookies.get('storedFavourites');

    if (storedFavourites) {
      return JSON.parse(storedFavourites);
    }

    return [];
  };

  const favouritesList = getFavouritesList();
  const isFavourite = favouritesList.includes(String(plot.id));

  // Recreating the rather complex rules of the old sitemap as to whether or not to show a price on the card
  const plotPrice = (plotDetails) => {
    if (plot?.status === 'sold') {
      return '';
    }

    // If they have entered offers over text in the CMS, show this instead
    if (plotDetails?.offers_over_price) {
      return `${plot.offers_over_text} ${new Intl.NumberFormat('en-GB', {
        currency: 'GBP',
        style: 'currency',
        minimumFractionDigits: 0,
      }).format(plotDetails.offers_over_price)}`;
    }

    // Otherwise use the base price field
    if (plotDetails?.price && plotDetails.price > 0) {
      return new Intl.NumberFormat('en-GB', {
        currency: 'GBP',
        style: 'currency',
        minimumFractionDigits: 0,
      }).format(plotDetails.price);
    }

    return '';
  };

  const availableForPersonalisation = plot.available_for_personalisation;
  const personalisationEndDate = plot.personalisation_end_date;
  let showPersonalisation = false;

  if (availableForPersonalisation) {
    showPersonalisation = true;

    if (
      personalisationEndDate &&
      personalisationEndDate.trim() !== '' &&
      personalisationEndDate !== 'false'
    ) {
      const [day, month, year] = personalisationEndDate.split('/');
      const endDate = new Date(year, month - 1, day);
      endDate.setHours(23, 59, 59, 999);

      const currentDate = new Date();

      showPersonalisation = currentDate <= endDate;
    }
  }

  return (
    <>
      <div
        className={`siteplan__card${isOpen ? ' siteplan__card--open' : ''}${
          className ? ` ${className}` : ''
        }`}
      >
        {plot?.status === 'sold' ? (
          <span className="siteplan__feature siteplan__feature--red">Sold</span>
        ) : (
          plot?.feature && (
            <span
              className={`siteplan__feature siteplan__feature--${plot.feature_color}`}
            >
              {plot.feature}
            </span>
          )
        )}

        {plot?.image && (
          <figure className="siteplan__figure">
            <img alt="Plot" className="siteplan__image" src={plot.image} />

            {plot.status !== 'sold' && (
              <button
                type="button"
                className={`siteplan__favouriteBtn favouriteBtn${
                  isFavourite ? ' favouriteBtn--active' : ''
                }`}
                data-id={plot.id}
                onClick={() => {
                  toggleFavourite(plot.id);
                }}
                aria-label="Toggle Favourite"
              >
                <svg
                  width="17"
                  height="16"
                  viewBox="-1 -1 19 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.51178 16L15.6648 8.18651C17.4511 6.25348 17.4458 3.26064 15.6442 1.36442C14.7717 0.484569 13.5977 0 12.338 0C11.152 0 10.0524 0.430075 9.15723 1.24491L8.49519 1.8503L7.8431 1.22916C7.02169 0.446497 5.8948 0.0150945 4.66965 0.0150945C3.36613 0.0150945 2.18348 0.502286 1.33946 1.38805C-0.444127 3.2673 -0.446847 6.24955 1.33348 8.1767L8.51178 16Z"
                    fill="currentColor"
                    stroke="black"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            )}
          </figure>
        )}

        <div className="siteplan__details">
          <div className="siteplan__plot-wrap">
            {plot?.title && (
              <h6 className="siteplan__plot-heading">{plot.title}</h6>
            )}

            {plot?.number && (
              <p className="siteplan__number">{`No. ${plot.number}`}</p>
            )}
          </div>

          <div className="siteplan__price-wrap">
            {plotPrice(plot) && (
              <p className="siteplan__price">{plotPrice(plot)}</p>
            )}
            {showPersonalisation && (
              <span className="siteplan__personalisation">
                <div className="siteplan__popover">
                  Personalisation choices available
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="9"
                    viewBox="0 0 15 9"
                    fill="none"
                    className="siteplan__popover-arrow"
                  >
                    <path
                      d="M13.1443 -1.5991e-06C14.0352 -1.5991e-06 14.4814 1.07714 13.8514 1.70711L7.78035 7.77818C7.38982 8.16871 6.75666 8.1687 6.36614 7.77818L0.295074 1.70711C-0.33489 1.07714 0.111276 -1.17763e-06 1.00218 -1.17763e-06L13.1443 -1.5991e-06Z"
                      fill="#1F2221"
                    />
                  </svg>
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  alt="Available for Personalisation"
                  className="siteplan__personalisation-icon"
                >
                  <path
                    d="M10.5506 11.4955H9.5249V9.96717C9.5249 9.21311 10.1384 8.59963 10.8924 8.59963H15.8819C16.0705 8.59963 16.2238 8.44628 16.2238 8.25775V3.723C16.2238 3.53447 16.0705 3.38112 15.8819 3.38112H13.3697V2.35547H15.8819C16.636 2.35547 17.2495 2.96894 17.2495 3.723V8.25775C17.2495 9.01181 16.636 9.62528 15.8819 9.62528H10.8924C10.7039 9.62528 10.5506 9.77863 10.5506 9.96717V11.4955Z"
                    fill="#74746D"
                  />
                  <path
                    d="M12.4975 5.73497H1.34004C0.60115 5.73497 0 5.13311 0 4.39329L7.12258e-05 1.34168C7.12258e-05 0.601861 0.601149 0 1.34004 0H8.8334V1.02565H1.34004C1.16667 1.02565 1.02565 1.16739 1.02565 1.34168V4.39329C1.02565 4.56751 1.16668 4.70932 1.34004 4.70932H12.4975C12.6708 4.70932 12.8119 4.56758 12.8119 4.39329V1.34168C12.8119 1.16746 12.6708 1.02565 12.4975 1.02565H8.52671V0H12.4975C13.2364 0 13.8375 0.601861 13.8375 1.34168V4.39329C13.8376 5.13303 13.2364 5.73489 12.4975 5.73489L12.4975 5.73497Z"
                    fill="#74746D"
                  />
                  <path
                    d="M10.0382 20C9.30368 20 8.61642 19.6825 8.1526 19.1289C7.70865 18.5991 7.52275 17.9089 7.64247 17.2352L8.21363 14.0231C8.32467 13.3988 8.29191 12.7457 8.1189 12.1345C8.01021 11.7504 8.08678 11.3465 8.32888 11.0263C8.57945 10.695 8.96486 10.5049 9.38622 10.5049H10.6897C11.111 10.5049 11.4964 10.6949 11.7471 11.0263C11.9892 11.3465 12.0657 11.7504 11.957 12.1345C11.7841 12.7457 11.7513 13.3988 11.8623 14.0231L12.4335 17.2352C12.5533 17.9088 12.3673 18.599 11.9233 19.1289C11.4597 19.6825 10.7725 20 10.0382 20ZM9.38637 11.5307C9.28929 11.5307 9.20212 11.5725 9.14713 11.6452C9.11372 11.6894 9.07946 11.7617 9.10596 11.8553C9.32092 12.6151 9.3616 13.4268 9.22355 14.2029L8.6524 17.415C8.58495 17.7946 8.68666 18.1694 8.93887 18.4704C9.20725 18.7908 9.60796 18.9745 10.0382 18.9745C10.4684 18.9745 10.8691 18.7908 11.1375 18.4704C11.3897 18.1694 11.4914 17.7946 11.4239 17.415L10.8528 14.2029C10.7148 13.4268 10.7554 12.615 10.9704 11.8553C10.9969 11.7616 10.9626 11.6893 10.9292 11.6451C10.8742 11.5724 10.787 11.5307 10.69 11.5307L9.38637 11.5307Z"
                    fill="#74746D"
                  />
                  <path
                    d="M2.63688 10.424C1.71963 10.424 0.974121 9.67737 0.974121 8.75969V5.55746H1.99977V8.75969C1.99977 9.1119 2.28531 9.39837 2.63617 9.39837C2.98781 9.39837 3.27335 9.1119 3.27335 8.75969V8.19053C3.27335 7.34643 3.95918 6.65967 4.80222 6.65967H4.96554C5.71846 6.65967 6.33101 7.273 6.33101 8.02684C6.33101 8.19764 6.39739 8.35819 6.51783 8.47884C6.63799 8.59921 6.79769 8.66552 6.96748 8.66552H6.96762C7.13742 8.66545 7.29711 8.59913 7.41727 8.47869C7.53771 8.35797 7.60394 8.19742 7.60394 8.02662L7.60366 6.83417C7.60323 5.98338 8.06578 5.19448 8.81087 4.77539L9.31372 5.66934C8.89135 5.90694 8.62909 6.35311 8.62938 6.8338L8.62967 8.0264C8.62981 8.47078 8.45716 8.88866 8.14348 9.20305C7.82959 9.51773 7.41214 9.6911 6.9679 9.69117H6.96754C6.52346 9.69117 6.10607 9.51795 5.79203 9.20349C5.4782 8.88917 5.3054 8.47129 5.3054 8.02684C5.3054 7.83852 5.15298 7.68532 4.96558 7.68532H4.80226C4.52476 7.68532 4.29904 7.91197 4.29904 8.19053V8.75969C4.2989 9.67737 3.55332 10.424 2.63685 10.424H2.63688Z"
                    fill="#74746D"
                  />
                </svg>
              </span>
            )}
          </div>

          <ul className="siteplan__plot-list">
            {plot?.bedrooms && (
              <li className="siteplan__plot-item">{`${plot.bedrooms} Bed`}</li>
            )}

            {plot?.garage_style && (
              <li className="siteplan__plot-item">{plot.garage_style.label}</li>
            )}

            {plot?.house_style && (
              <li className="siteplan__plot-item">{plot.house_style.label}</li>
            )}
          </ul>

          {plot?.phase && <p className="siteplan__phase">{plot.phase}</p>}

          {plot.ready_to_move_into === true && (
            <div className="siteplan__ready-to-move-into">
              <span className="siteplan__ready-to-move-into-span">
                Ready to Move Into
              </span>
            </div>
          )}
        </div>

        <div className="siteplan__footer">
          {plot?.url && plot.status !== 'sold' && (
            <a className="button siteplan__link" href={plot.url}>
              View Home
            </a>
          )}

          <button
            className="button button--light siteplan__plot-close"
            onClick={() => {
              onPlotClose(false);
              onPlotChange(undefined);
            }}
            type="button"
          >
            Close
          </button>
        </div>
      </div>

      <button
        aria-label="Close plot card"
        className={`siteplan__close-overlay${
          isOpen ? ' siteplan__close-overlay--active' : ''
        }`}
        onClick={() => {
          onPlotClose(false);
          onPlotChange(undefined);
        }}
        type="button"
      />
    </>
  );
}
